/**
 * RRWebRecorder — Wraps rrweb's record() to capture DOM mutations,
 * mouse events, scroll, and input for streaming to the vision backend.
 *
 * Masks sensitive inputs (passwords, emails, phone numbers).
 * Excludes the widget's own DOM (#holostaff-widget-root).
 * Full snapshot every 30s for replay drift correction.
 */

import { record } from 'rrweb'
import type { eventWithTime } from '@rrweb/types'
import { shieldCrossOriginImages } from './imageShield'

const CHECKOUT_INTERVAL_MS = 30_000

// rrweb constants
const RRWEB_INCREMENTAL = 3
const SRC_MUTATION = 0

/**
 * Check if an IncrementalSnapshot is a style-only mutation (GSAP, CSS transitions, popmotion).
 * These fire 60 times/sec but tell the VLM nothing about user behavior.
 * Structural mutations (node adds/removes, text changes, class changes) are preserved.
 */
function isStyleOnlyMutation(event: eventWithTime): boolean {
  if (event.type !== RRWEB_INCREMENTAL) return false
  const data = (event as any).data
  if (!data || data.source !== SRC_MUTATION) return false

  const { adds, removes, texts, attributes } = data
  if ((adds && adds.length) || (removes && removes.length) || (texts && texts.length)) return false

  // Only attribute mutations — check if ALL are just `style` changes
  if (attributes && attributes.length > 0) {
    return attributes.every(
      (attr: any) => attr.attributes && Object.keys(attr.attributes).length === 1 && 'style' in attr.attributes,
    )
  }

  return true // empty mutation
}

/**
 * Patch non-Element node prototypes so rrweb's blockSelector check
 * doesn't throw "i.matches is not a function" on text/comment nodes.
 * rrweb calls node.matches(blockSelector) without checking nodeType first.
 */
function patchNonElementMatches(): void {
  for (const Proto of [Text, Comment, CDATASection, ProcessingInstruction]) {
    if (!(Proto.prototype as any).matches) {
      (Proto.prototype as any).matches = function () { return false }
    }
  }
}

export class RRWebRecorder {
  /** Called for each rrweb event. Wire this to the WebSocket client. */
  onEvent: ((event: eventWithTime) => void) | null = null

  private stopFn: (() => void) | null = null
  private stopShield: (() => void) | null = null
  private _recording = false
  private _paused = false

  get isRecording(): boolean {
    return this._recording
  }

  get isPaused(): boolean {
    return this._paused
  }

  /** Start recording the host page. */
  start(): void {
    if (this._recording) return

    // Patch non-Element nodes before rrweb attaches its MutationObserver
    patchNonElementMatches()

    // Shield cross-origin <img> elements from rrweb's `crossOrigin` mutation
    // before rrweb takes its FullSnapshot (I-015g). Without this, host-page
    // cross-origin images load-and-blank because the browser caches the
    // failed CORS response that rrweb's mutation triggered.
    this.stopShield = shieldCrossOriginImages()

    const stop = record({
      emit: (event: eventWithTime) => {
        if (this._paused) return
        if (isStyleOnlyMutation(event)) return // drop GSAP/CSS animation noise
        this.onEvent?.(event)
      },
      checkoutEveryNms: CHECKOUT_INTERVAL_MS,
      // Block video elements (massive mutation noise), widget, and sensitive fields
      blockClass: 'hs-no-record',
      blockSelector: '#holostaff-widget-root, #holostaff-agent, [id^="anam-avatar"], video, [autocomplete^="cc-"], [data-hs-no-record]',
      // Mask sensitive input types + elements marked with data-hs-mask
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
      },
      maskTextSelector: '[data-hs-mask]',
      // Throttle high-frequency events to reduce volume
      sampling: {
        mousemove: 100,
        scroll: 150,
        input: 'last',
      },
      // Inline external resources into the snapshot. Without these, the
      // BB Replayer's iframe (about:blank, null origin) fails CORS when
      // fetching the host page's CSS/images/fonts — the replay renders
      // structurally but unstyled (looks blank). Tradeoff: the first
      // FullSnapshot grows substantially. Acceptable for v1 dogfood.
      inlineStylesheet: true,
      inlineImages: true,
      collectFonts: true,
    })

    if (stop) {
      this.stopFn = stop
      this._recording = true
      this._paused = false
      console.log('[RRWebRecorder] Started recording')
    } else {
      console.warn('[RRWebRecorder] record() returned undefined — recording may have failed')
    }
  }

  /** Stop recording and release resources. */
  stop(): void {
    if (!this._recording) return
    this.stopFn?.()
    this.stopFn = null
    this.stopShield?.()
    this.stopShield = null
    this._recording = false
    this._paused = false
    console.log('[RRWebRecorder] Stopped recording')
  }

  /** Pause event emission (recording continues internally but events are discarded). */
  pause(): void {
    if (!this._recording || this._paused) return
    this._paused = true
    console.log('[RRWebRecorder] Paused')
  }

  /** Resume event emission. */
  resume(): void {
    if (!this._recording || !this._paused) return
    this._paused = false
    // Take a fresh full snapshot so the replay can resync
    record.takeFullSnapshot()
    console.log('[RRWebRecorder] Resumed (full snapshot taken)')
  }
}
